import { AudienceGrowthChartView } from './AudienceGrowthChart.view';
import React, { useContext } from 'react';
import { addDays, differenceInYears, endOfMonth, endOfWeek, format, isSameDay, parse } from 'date-fns';
import { AnalyticsContext } from '../../../AnalyticsProvider';
import { ChartLinePoint, getHasFillingDataProps } from '../../../LineChart/LineChart.controller';
import { ProfileFollowersStats, StartOfWeekFormatSetting } from '../../../../../models/graphql';
import { getIsGroupedByWeek } from 'modules/Analyze/helpers/chartHelpers';
import { cloneDeep } from 'lodash';
import { DateStringFormat } from 'constants/time-formats';
import { useSettings } from '../../../../../hooks/useSettings';

type AudienceGrowthChartControllerProps = {
  followersByProfile: ProfileFollowersStats[] | undefined;
  loading: boolean;
};

export const normalizeProfilesFollowersCount = (
  followersByProfile: ProfileFollowersStats[] | undefined,
  shouldUseInitialFollowers: boolean,
  hasPrefill: boolean,
  shouldAddFirstDay: boolean,
  shouldRemoveSecondDay: boolean
): ChartLinePoint[][] => {
  if (!followersByProfile) {
    return [[]];
  }
  const copyFollowersByProfile = cloneDeep(followersByProfile);
  if (shouldAddFirstDay) {
    const firstDay = copyFollowersByProfile[0].followersStats[0].rangeStartDate;
    copyFollowersByProfile.forEach(followersProfile => {
      const firstDayFollowersStats: ProfileFollowersStats['followersStats'][0] = {
        rangeStartDate: firstDay,
        rangeEndDate: firstDay,
        followers: followersProfile.previousPeriodStats?.followers
      };
      followersProfile.followersStats.unshift(firstDayFollowersStats);

      if (hasPrefill) {
        if (!shouldRemoveSecondDay) {
          const parsedPrefillDate = parse(
            copyFollowersByProfile[0].followersStats[1].rangeEndDate,
            DateStringFormat,
            new Date()
          );

          const endPrefillDate = addDays(parsedPrefillDate, 1);
          followersProfile.followersStats[1].rangeEndDate = format(endPrefillDate, DateStringFormat);
        } else {
          followersProfile.followersStats.splice(1, 1);
        }
      }
    });
  }

  return copyFollowersByProfile.map((profile, indexProfile) => {
    const initialFollowersProfile = profile.previousPeriodStats?.followers;
    const isInitialFollowersValid = initialFollowersProfile && initialFollowersProfile >= 1;
    let baseValue = copyFollowersByProfile[indexProfile].followersStats.find(
      profile => profile.followers && profile.followers >= 1
    )?.followers;

    if (shouldUseInitialFollowers && isInitialFollowersValid && !hasPrefill) {
      baseValue = initialFollowersProfile;
    }

    return profile.followersStats.map((followersObj, index) => {
      const qtyFollowersDay = followersObj.followers;

      if (qtyFollowersDay === 0) {
        return { rawValue: 0, normalizedValue: 0 };
      }

      if ((!baseValue && baseValue !== 0) || !qtyFollowersDay) {
        return { rawValue: undefined, normalizedValue: undefined };
      }

      if (index === 0 && !isInitialFollowersValid) {
        return { rawValue: qtyFollowersDay, normalizedValue: 0 };
      }
      const normalizedFollowersValue = (qtyFollowersDay * 100) / baseValue;
      const roundedValue = Math.round(normalizedFollowersValue * 100 + Number.EPSILON) / 100; // to round correctly

      return { rawValue: qtyFollowersDay, normalizedValue: roundedValue - 100 };
    });
  });
};

export const AudienceGrowthChartController = ({ followersByProfile, loading }: AudienceGrowthChartControllerProps) => {
  const { startDate, endDate } = useContext(AnalyticsContext);
  const currentDate = new Date();
  const { selectedOrgSettings } = useSettings();
  const dayStartOfWeek = selectedOrgSettings?.startOfWeekFormat ?? StartOfWeekFormatSetting.SUNDAY;
  if (followersByProfile && followersByProfile.length >= 1) {
    const isEveryElementNull = followersByProfile.every(
      profileFollowers => !profileFollowers.followersStats.some(followerStat => followerStat.followers !== null)
    );
    const firstDay = parse(followersByProfile[0].followersStats[0].rangeStartDate, DateStringFormat, currentDate);
    const firstFollowersByProfile = followersByProfile[0].followersStats;
    const profileIds = followersByProfile.map(followersProfile => followersProfile.socialProfileId);
    const isGroupedByWeek = getIsGroupedByWeek(startDate, endDate);
    const isGroupByMonth = Math.abs(differenceInYears(startDate, endDate)) >= 1;
    const followersStats = followersByProfile[0].followersStats;
    const { hasPreFillingData } = getHasFillingDataProps(startDate, endDate, dayStartOfWeek);
    const initialFollowers = followersByProfile.reduce(
      (previousObj, profile) => ({ ...previousObj, [profile.socialProfileId]: profile.previousPeriodStats?.followers }),
      {}
    );
    const shouldRemoveSecondDay = isSameDay(
      startDate,
      isGroupByMonth
        ? endOfMonth(startDate)
        : endOfWeek(startDate, { weekStartsOn: dayStartOfWeek === StartOfWeekFormatSetting.SUNDAY ? 0 : 1 })
    );
    const secondDay = followersByProfile[0].followersStats.at(1) ?? followersByProfile[0].followersStats[0];
    const startDateFromSecondPeriod = parse(secondDay.rangeStartDate, DateStringFormat, currentDate);
    const firstDateFromData = followersStats[0].rangeStartDate;
    const lastDateFromData = followersStats[followersStats.length - 1].rangeEndDate;

    const shouldAddFirstDay =
      (!hasPreFillingData && initialFollowers && (isGroupByMonth || isGroupedByWeek)) || hasPreFillingData;
    const dates = firstFollowersByProfile.map(followersObj =>
      parse(followersObj.rangeEndDate, DateStringFormat, currentDate)
    );
    if (shouldAddFirstDay) {
      if (hasPreFillingData) {
        dates[0] = startDateFromSecondPeriod;
        if (shouldRemoveSecondDay) {
          dates.splice(1, 1);
        }
      }
      dates.unshift(firstDay);
    }

    return (
      <AudienceGrowthChartView
        dates={dates}
        isEmpty={isEveryElementNull}
        loading={loading}
        profileIds={profileIds}
        firstDateFromData={firstDateFromData}
        lastDateFromData={lastDateFromData}
        initialFollowers={initialFollowers}
        getValuesForChartCb={() =>
          normalizeProfilesFollowersCount(
            followersByProfile,
            isGroupedByWeek || isGroupByMonth,
            hasPreFillingData,
            shouldAddFirstDay,
            shouldRemoveSecondDay
          )
        }
      />
    );
  }

  return (
    <AudienceGrowthChartView
      dates={[]}
      isEmpty={true}
      loading={loading}
      profileIds={[]}
      firstDateFromData={''}
      lastDateFromData={''}
      initialFollowers={{}}
    />
  );
};
